import React from "react";
import CanvasAxisLabels from "./CanvasAxisLabels";
import EditorViewport from "./EditorViewport";
import DraggableStation from "../DraggableStation";
import DraggableSymbol from "../DraggableSymbol";
import AreaPolygonEditor from "./AreaPolygonEditor";
import { STATION_HALF_SIZE } from "../../utils/constants";
import { SYMBOL_HALF_SIZE, getSymbolById } from "../../utils/symbolTypes";
import { canvasMode } from "../../utils/editorCursor";

const PlaneCanvas = ({
  dimensions,
  canvasSize,
  canvasRef,
  imageUrl,
  placedStations,
  stationsVisible = true,
  selectedStationId,
  setSelectedStationId,
  handleDragStop,
  activeLayoutType,
  viewport,
  placedSymbols,
  symbolsVisible,
  handleSymbolDragStop,
  onCanvasClick,
  onCanvasMouseMove,
  onDeleteSymbol,
  areaEditMode = false,
  areaVisible = true,
  boundaryVertices = [],
  boundaryDrawing = false,
  onMoveVertex,
  onRemoveVertex,
  onVertexClick,
  tripwireLayer = null,
  tripwiresVisible = true,
  // 圖層's 顯示 rows (editorLayerToggles) for this layout.
  layerToggles,
  // useEditorMode result: the zoom/pan view, the map layers and whether the
  // plan may be edited (瀏覽 vs 編輯模式). Absent = editable at zoom 1.
  editor,
}) => {
  const editing = editor?.editing ?? true;
  // 瀏覽: the glyphs keep their popovers and still let a press through to
  // the canvas pan, but cannot move (axis "none") and report nothing.
  const lockedDrag = editing ? {} : { axis: "none", onDragStop: () => {} };
  const cursorMode = canvasMode(editing, !!onCanvasClick);

  // Stations/symbols carry METERS only; the pixel position is projected here,
  // at render time. `viewport.project` yields the TRUE canvas position
  // (see utils/viewport.js); react-draggable places a glyph by its top-left
  // corner, so shift by the glyph's OWN half size to center it on its point.
  // Symbols use SYMBOL_HALF_SIZE (24px box), matching how Line/Tunnel canvas
  // render them - the old projection gave them the station's 6px, which left
  // plane symbols 6px off their stored point.
  const toGlyphPosition = (positionMeters, halfSize) => {
    if (!positionMeters || !viewport) return { x: 50, y: 50 };
    const { x, y } = viewport.project(positionMeters);
    return { x: x - halfSize, y: y - halfSize };
  };

  // Bounds are expressed in the same glyph/top-left space as toGlyphPosition's
  // output, which is what react-draggable constrains: the part of the floor
  // that is visible. At zoom 1 that is the canvas box (the floor fills it);
  // zoomed in, the visible canvas; zoomed out over the map, the floor.
  const getBounds = (halfSize) => {
    const floor = viewport.floorRect;
    return {
      left: Math.max(floor.left, 0) - halfSize,
      top: Math.max(floor.top, 0) - halfSize,
      right: Math.min(floor.left + floor.width, canvasSize.width) - halfSize,
      bottom: Math.min(floor.top + floor.height, canvasSize.height) - halfSize,
    };
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <CanvasAxisLabels
        dimensions={dimensions}
        canvasSize={canvasSize}
        viewport={viewport}
      />

      <EditorViewport
        canvasRef={canvasRef}
        editor={editor}
        viewport={viewport}
        canvasSize={canvasSize}
        imageUrl={imageUrl}
        dimensions={dimensions}
        onClick={editing ? onCanvasClick : null}
        onMouseMove={editing ? onCanvasMouseMove : undefined}
        layerToggles={layerToggles}
      >
        {areaVisible && (
          <AreaPolygonEditor
            layer="fill"
            vertices={boundaryVertices}
            drawing={boundaryDrawing}
            viewport={viewport}
            canvasSize={canvasSize}
          />
        )}

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {symbolsVisible &&
            placedSymbols &&
            placedSymbols
              .filter((symbol) => {
                const symbolType = getSymbolById(symbol.symbol_type);
                return symbolType?.compatibleLayouts?.includes("plane");
              })
              .map((symbol) => (
                <DraggableSymbol
                  key={symbol.id || `symbol-${symbol.symbol_type}`}
                  symbol={symbol}
                  layoutType="plane"
                  activeLayoutType={activeLayoutType}
                  position={toGlyphPosition(
                    symbol.position_meters,
                    SYMBOL_HALF_SIZE,
                  )}
                  bounds={getBounds(SYMBOL_HALF_SIZE)}
                  onDragStop={handleSymbolDragStop}
                  onDelete={onDeleteSymbol}
                  cursorMode={cursorMode}
                  {...lockedDrag}
                />
              ))}
          {stationsVisible &&
            placedStations.map((station) => (
              <DraggableStation
                key={station.id || `station-${station.device_id || station.name}`}
                station={station}
                layoutType="plane"
                activeLayoutType={activeLayoutType}
                position={toGlyphPosition(
                  station.position_meters,
                  STATION_HALF_SIZE,
                )}
                bounds={getBounds(STATION_HALF_SIZE)}
                selectedStationId={selectedStationId}
                setSelectedStationId={setSelectedStationId}
                onDragStop={handleDragStop}
                pxPerMeter={viewport.pxPerMeter}
                cursorMode={cursorMode}
                {...lockedDrag}
              />
            ))}
        </div>

        {areaVisible && (
          <AreaPolygonEditor
            layer="handles"
            vertices={boundaryVertices}
            drawing={boundaryDrawing}
            editable={areaEditMode}
            viewport={viewport}
            canvasSize={canvasSize}
            dimensions={dimensions}
            onMoveVertex={onMoveVertex}
            onRemoveVertex={onRemoveVertex}
            onVertexClick={onVertexClick}
          />
        )}
        {tripwiresVisible && tripwireLayer}
      </EditorViewport>
    </div>
  );
};

export default PlaneCanvas;
